/**
 * Bundle 136 P0-4 - QA cockpit.
 *
 * Preview-only super-admin surface that renders one tile per
 * `POST /admin/qa/*` route. Fixes the "no hidden dashboards" rule:
 * Bundle 134 patch #3 shipped `POST /admin/qa/seed-reminder-cohort`
 * and `POST /admin/qa/purge-reminder-cohort` with no UI - matchmakers
 * and QA had to hit them via curl.
 *
 * The list of tiles is DRIVEN by `GET /admin/qa/registry` so any
 * future QA helper automatically shows up here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { RefreshCw, PlayCircle } from 'lucide-react';

// Bundle 140 P0-2 - per-tile operational guidance. Keyed on the
// endpoint path so any newly-registered QA helper can add its own
// "when to use" entry here without touching the tile component.
const TILE_GUIDANCE = {
  '/api/admin/qa/seed-reminder-cohort': {
    when_to_use:
      'When to use: before verifying survey reminder filters. '
      + 'Seeds one invitation per skip category (eligible, submitted, '
      + 'opted_out, blank email, invalid email, unresolvable '
      + 'wildcards) so QA can walk the full reminder path in one '
      + 'click.',
  },
  '/api/admin/qa/purge-reminder-cohort': {
    when_to_use:
      'When to use: after a reminder verification run to clean up '
      + 'the seeded rows.',
  },
  '/api/admin/qa/age-gate-preflight': {
    when_to_use:
      'When to use: before flipping AGE_GATE_ENABLED on prod. Runs '
      + 'the 5 enforcement scenarios directly against the service '
      + 'function so ops can prove the code path is armed BEFORE '
      + 'real users see the modal.',
  },
};

const AdminQAPage = () => {
  const [routes, setRoutes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: reg }, { data: evs }] = await Promise.all([
        api.get('/admin/qa/registry'),
        api.get('/admin/events', { params: { status: 'published', limit: 100 } }),
      ]);
      setRoutes(reg?.routes || []);
      setEvents(evs?.events || evs || []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Failed to load QA cockpit',
      );
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto"
          data-testid="admin-qa-cockpit-page">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-secondary-800 mb-1">
            QA Cockpit * preview only
          </div>
          <h1 className="font-display text-3xl font-bold text-primary-900" data-testid="qa-cockpit-title">
            QA Cockpit
          </h1>
          <p className="text-neutral-600 text-sm mt-1 max-w-3xl" data-testid="qa-cockpit-purpose">
            This page is for verifying features in preview BEFORE they hit prod. Every tile
            here either seeds test data or runs a dry-run of a production-critical code
            path. These endpoints are refused on dostination.com; running them in
            production has no effect.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load}
                    disabled={loading}
                    data-testid="qa-cockpit-refresh-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Bundle 140 P0-2 - suggested verification runs. Static list
          for the common walk-throughs. Update as new flows land. */}
      <Card className="mb-4 bg-neutral-50 border-neutral-200"
                data-testid="qa-cockpit-suggested-runs">
        <CardContent className="p-4 text-sm text-primary-800">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">
            Suggested verification runs
          </div>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Reminder-filter smoke: click Seed reminder cohort {'->'} run send-reminder on the target event {'->'} Purge reminder cohort.</li>
            <li>Age-gate preflight: click Age gate prod flip dry run before flipping AGE_GATE_ENABLED via /admin/settings/feature-flags.</li>
            <li>Framing sentinel wire: /admin/health {'->'} Run alert test to prove Sentry/PagerDuty transports.</li>
          </ol>
        </CardContent>
      </Card>

      <div className="grid gap-3 md:grid-cols-2"
            data-testid="qa-cockpit-tiles">
        {routes.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-neutral-500 text-sm"
                              data-testid="qa-cockpit-empty">
              No QA helpers registered.
            </CardContent>
          </Card>
        ) : null}
        {routes.map((r) => (
          <QATile key={r.path} route={r} events={events}
                    guidance={TILE_GUIDANCE[r.path]}
                    onOutput={setOutput} />
        ))}
      </div>

      {output ? (
        <Card className="mt-4" data-testid="qa-cockpit-output">
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
              Last run output
            </div>
            <pre className="text-xs bg-neutral-50 border border-neutral-200 rounded p-2 max-h-96 overflow-auto"
                     data-testid="qa-cockpit-output-pre">
              {JSON.stringify(output, null, 2)}
            </pre>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};


const QATile = ({ route, events, guidance, onOutput }) => {
  const [eventId, setEventId] = useState('');
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);

  const needsEvent = String(route.path).includes('reminder-cohort');
  const needsCount = String(route.path).includes('seed-reminder-cohort');

  const run = async () => {
    setBusy(true);
    try {
      const body = {};
      if (needsEvent) body.event_id = eventId;
      if (needsCount) body.count_per_category = Number(count) || 1;
      const path = route.path.replace(/^\/api/, '');
      const { data } = await api.post(path, body);
      onOutput(data);
      toast.success(`${route.name || route.path} OK`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
      onOutput({ error: e?.response?.data?.detail || String(e) });
    } finally { setBusy(false); }
  };

  const disabled = busy || (needsEvent && !eventId);
  const tid = String(route.name || route.path).replace(/[^a-z0-9]+/gi, '-');

  return (
    <Card data-testid={`qa-tile-${tid}`}>
      <CardContent className="p-4 space-y-3">
        <div className="font-semibold text-primary-900 text-sm"
                data-testid={`qa-tile-name-${tid}`}>
          {route.name || route.path.split('/').slice(-1)[0]}
        </div>
        <div className="text-xs text-neutral-500 font-mono break-all">
          {route.path}
        </div>
        {route.summary ? (
          <div className="text-xs text-neutral-600 leading-relaxed">
            {route.summary}
          </div>
        ) : null}
        {guidance?.when_to_use ? (
          <div
            data-testid={`qa-tile-when-${tid}`}
            className="text-[11px] leading-relaxed rounded border border-secondary-200 bg-secondary-50 text-secondary-900 px-2.5 py-1.5"
          >
            {guidance.when_to_use}
          </div>
        ) : null}
        {needsEvent ? (
          <div>
            <Label className="text-xs" htmlFor={`qa-tile-event-${tid}`}>
              Event
            </Label>
            <select
              id={`qa-tile-event-${tid}`}
              aria-label="Event"
              className="w-full border border-neutral-200 rounded px-2 py-1 text-sm"
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              data-testid={`qa-tile-event-${tid}`}
            >
              <option value="">Pick an event...</option>
              {(events || []).map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title || ev.slug || ev.id}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {needsCount ? (
          <div>
            <Label className="text-xs" htmlFor={`qa-tile-count-${tid}`}>
              Count per category
            </Label>
            <Input id={`qa-tile-count-${tid}`}
                        aria-label="Count per category"
                        type="number" min={1} max={5} value={count}
                        onChange={(e) => setCount(e.target.value)}
                        data-testid={`qa-tile-count-${tid}`} />
          </div>
        ) : null}
        <div className="flex justify-end">
          <Button size="sm" onClick={run} disabled={disabled}
                        className="bg-primary-900 text-white hover:bg-primary-800"
                        data-testid={`qa-tile-run-${tid}`}>
            <PlayCircle className="h-3.5 w-3.5 mr-1" />
            {busy ? 'Running...' : 'Run'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


export default AdminQAPage;
